import { getAccessToken, getRefreshToken, redirectToCasLogin, refreshAccessToken } from "./tokenRefresh";

/** 刷新接口自身不劫持（它走裸实例，有自己的重读重试逻辑） */
const REFRESH_URL_PART = "/authmanage/auth/refreshToken";

/** 401轮需要吞掉的其他终态事件（readystatechange在构造期单独处理） */
const SWALLOW_EVENTS = ["load", "loadend", "error", "timeout"];

/**
 * 全局XHR劫持：qiankun子应用自带axios，主应用拦截器罩不住它们发的请求。
 * 在子应用加载前把 window.XMLHttpRequest 换成子类，在请求底层统一兜底：
 *
 *   任何携带ACCESS_TOKEN的请求收到401
 *     → 吞掉本轮终态事件（上层axios/子应用无感知）
 *     → 静默单飞换新token
 *     → 原样重放（方法/URL/请求头/请求体快照，ACCESS_TOKEN换新值）
 *     → 重放结果作为最终响应交给上层
 *
 * 这样主应用与所有子应用（含未来新增）一处覆盖，子应用零改动。
 * 兜底关系：主应用axios自己的401拦截器保留——若本补丁放行了401
 * （重放过仍401/匿名请求），上层拦截器仍是最后防线。
 */
export function installXhrAuthPatch() {
  const w = window as any;
  if (w.__xhrAuthPatched) {
    return;
  }
  // 应急开关：运行时注入 _CONFIG.disableXhrAuthPatch=true 可整体关闭，恢复原生行为
  if (w._CONFIG && w._CONFIG.disableXhrAuthPatch === true) {
    return;
  }
  w.__xhrAuthPatched = true;

  const NativeXHR = w.XMLHttpRequest;

  class PatchedXHR extends NativeXHR {
    // 请求快照（重放用）
    public __method = "";
    public __url = "";
    public __async = true;
    public __headers: { [name: string]: string } = {};
    public __body: any = null;
    // 状态：__swallow=true时吞掉本轮终态事件；__retried=true表示已重放过，再401放行
    public __swallow = false;
    public __retried = false;

    constructor(...args: any[]) {
      super(...args);
      // 必须在构造期最先注册：应用的on*赋值/晚注册的listener都排在后面，
      // 我们才有机会在401轮用stopImmediatePropagation把终态吞在上层看到之前
      this.addEventListener("readystatechange", this.__onStateChange);
      SWALLOW_EVENTS.forEach((evt) => this.addEventListener(evt, this.__swallowIfActive));
    }

    public open(...args: any[]) {
      this.__method = args[0];
      this.__url = String(args[1] || "");
      this.__async = args.length < 3 || args[2] !== false;
      super.open.apply(this, args);
    }

    public setRequestHeader(name: string, value: string) {
      // 记录快照供重放（名字统一小写，重放时原样回设，大小写不敏感）
      this.__headers[String(name).toLowerCase()] = value;
      super.setRequestHeader(name, value);
    }

    public send(body?: any) {
      this.__body = body;
      super.send(body);
    }

    private __onStateChange = (ev: Event) => {
      if (this.__swallow) {
        ev.stopImmediatePropagation();
        return;
      }
      if (this.readyState !== 4 || this.status !== 401 || this.__retried) {
        return;
      }
      // 只劫持"带凭证的认证请求"：匿名请求401按原样放行（如登录流程）
      if (!this.__headers["access_token"]) {
        return;
      }
      if (this.__url.indexOf(REFRESH_URL_PART) !== -1) {
        return;
      }
      if (!getRefreshToken()) {
        return;
      }
      // 吞401轮 → 静默换新 → 原样重放
      this.__swallow = true;
      ev.stopImmediatePropagation();
      refreshAccessToken().then((ok) => {
        this.__swallow = false;
        if (!ok) {
          // refresh_token也过期了：跳CAS重新登录（与主应用401终态行为一致）
          redirectToCasLogin();
          return;
        }
        this.__retried = true;
        this.__headers["access_token"] = String(getAccessToken() || "");
        this.open(this.__method, this.__url, this.__async);
        Object.keys(this.__headers).forEach((name) => {
          super.setRequestHeader.call(this, name, this.__headers[name]);
        });
        this.send(this.__body);
      });
    };

    private __swallowIfActive = (ev: Event) => {
      if (this.__swallow) {
        ev.stopImmediatePropagation();
      }
    };
  }

  w.XMLHttpRequest = PatchedXHR;
}
